import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setUserData } from "../redux/userRedux/userActions";
import { postData } from "../services/api.service";
import instanceAPI from "../api/axios.service.js";
import { swalAlertDanger, swalAlertSuccess } from "../components/common/Alerts";
import "../styles/assets/colors.css";
import "../styles/Form.css";
import BackButton from "../components/common/BackButton";
import { Person, Telephone, Envelope } from "react-bootstrap-icons";

const Profile = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user.dataUser);
  const [loading, setLoading] = useState(false);
  // Estados locales para los campos editables
  const [name, setName] = useState(user?.first_name || "");
  const [phone, setPhone] = useState(user?.phone_number || "");
  const [editing, setEditing] = useState(false);

  // Verificar si el usuario se logueó con Google y cargar datos si es necesario
  useEffect(() => {
    const checkSession = async () => {
      if (!user) {
        setLoading(true);
        try {
          const response = await instanceAPI.get("/auth/me");
          if (response.data.success && response.data.user) {
            const userData = response.data.user;
            dispatch(
              setUserData({
                first_name: userData.first_name,
                last_name: userData.last_name,
                email: userData.email,
                role_id: userData.role_id,
                phone_number: userData.phone_number || "",
                provider: userData.provider || "LOCAL",
              })
            );
            setName(userData.first_name + " " + userData.last_name || "");
            setPhone(userData.phone_number || "");
          }
        } catch (error) {
        } finally {
          setLoading(false);
        }
      } else {
        setName(user.first_name + " " + user.last_name || "");
        setPhone(user.phone_number || "");
      }
    };

    checkSession();
  }, [user, dispatch]);

  // Determinar si el usuario puede editar (solo usuarios locales pueden editar)
  const canEdit = user?.provider !== "GOOGLE";

  const handleSave = async () => {
    try {
      const response = await postData("/user_update_data", {
        first_name: name,
        phone_number: phone,
        email: user.email,
      });
      if (response.data.success) {
        dispatch(
          setUserData({ ...user, first_name: name, phone_number: phone })
        );
        setEditing(false);
        swalAlertSuccess("Datos actualizados correctamente", "", () => {});
      } else {
        swalAlertDanger(
          response.data.message || "No se pudieron guardar los cambios.",
          "",
          () => {}
        );
      }
    } catch (error) {
      console.error("Error al guardar:", error);
      swalAlertDanger(
        "Error al guardar los datos. Intenta nuevamente.",
        "Si el error persiste, comuníquese con el administrador",
        () => {}
      );
    }
  };

  const handleCancel = () => {
    setName(user.first_name || "");
    setPhone(user.phone_number || "");
    setEditing(false);
  };

  // Mostrar loading si no hay usuario y se está cargando
  if (loading || !user) {
    return (
      <div className="bg-orange min-vh-100 text-white position-relative d-flex justify-content-center align-items-center">
        <div className="text-center">
          <div className="spinner-border text-white" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-3">Cargando datos del usuario...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-orange min-vh-100 text-white position-relative">
      {/* Header con botón de regreso */}
      <div className="mb-4 py-3 ps-3">
        <BackButton />
      </div>

      {/* Contenedor principal */}
      <div className="container-fluid">
        <div className="row justify-content-center">
          <div className="col-12 col-md-6 col-lg-4">
            <div
              className="bg-dark rounded-4 p-4 mx-3 mx-md-0"
              style={{ borderRadius: "25px" }}
            >
              {/* Header del perfil */}
              <div className="text-center mb-4">
                <div
                  className="bg-orange rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                  style={{ width: "80px", height: "80px" }}
                >
                  <Person size={40} className="text-white" />
                </div>
                <h2 className="h3 text-white mb-1">Mi Perfil</h2>
                <p className="text-muted small">
                  Gestiona tu información personal
                </p>
              </div>

              {/* Información del usuario */}
              <div className="mb-4">
                <div className="d-flex align-items-center mb-3 p-3 bg-cream rounded-3">
                  <Envelope className="text-dark me-3" size={20} />
                  <div>
                    <label className="form-label text-dark mb-1 fw-bold small">
                      Email
                    </label>
                    <p className="text-dark mb-0">{user.email}</p>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label text-white mb-2 fw-bold d-flex align-items-center">
                    <Person className="me-2" size={18} />
                    Nombre
                    {!canEdit && (
                      <span className="ms-2 badge bg-info small">Google</span>
                    )}
                  </label>
                  <input
                    type="text"
                    className={`form-control ${
                      editing && canEdit ? "login-input" : "bg-cream border-0"
                    }`}
                    value={name}
                    disabled={!editing || !canEdit}
                    onChange={(e) => setName(e.target.value)}
                    style={{
                      backgroundColor:
                        editing && canEdit
                          ? "transparent"
                          : "var(--color-cream)",
                      color: editing && canEdit ? "#fff" : "#000",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      border:
                        editing && canEdit
                          ? "1px solid var(--color-orange)"
                          : "none",
                      opacity: !canEdit ? 0.7 : 1,
                    }}
                  />
                </div>

                <div className="mb-4">
                  <label className="form-label text-white mb-2 fw-bold d-flex align-items-center">
                    <Telephone className="me-2" size={18} />
                    Teléfono
                    {!canEdit && (
                      <span className="ms-2 badge bg-info small">Google</span>
                    )}
                  </label>
                  <input
                    type="text"
                    className={`form-control ${
                      editing && canEdit ? "login-input" : "bg-cream border-0"
                    }`}
                    value={phone}
                    disabled={!editing || !canEdit}
                    onChange={(e) => setPhone(e.target.value)}
                    style={{
                      backgroundColor:
                        editing && canEdit
                          ? "transparent"
                          : "var(--color-cream)",
                      color: editing && canEdit ? "#fff" : "#000",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      border:
                        editing && canEdit
                          ? "1px solid var(--color-orange)"
                          : "none",
                      opacity: !canEdit ? 0.7 : 1,
                    }}
                  />
                  {!canEdit && (
                    <small className="text-muted mt-1 d-block">
                      Los usuarios que iniciaron sesión con Google no pueden
                      editar sus datos aquí.
                    </small>
                  )}
                </div>

                {/* Botones de acción */}
                <div className="d-flex gap-2 justify-content-center">
                  {canEdit ? (
                    !editing ? (
                      <button
                        className="btn btn-lg px-4 py-2 fw-bold"
                        onClick={() => setEditing(true)}
                        style={{
                          backgroundColor: "var(--color-orange)",
                          border: "none",
                          borderRadius: "25px",
                          color: "white",
                          minWidth: "120px",
                          transition: "all 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = "var(--color-red)";
                          e.target.style.transform = "translateY(-2px)";
                          e.target.style.boxShadow =
                            "0 6px 12px rgba(243, 145, 73, 0.3)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor =
                            "var(--color-orange)";
                          e.target.style.transform = "translateY(0)";
                          e.target.style.boxShadow = "none";
                        }}
                      >
                        ✏️ Editar
                      </button>
                    ) : (
                      <>
                        <button
                          className="btn btn-lg px-4 py-2 fw-bold"
                          onClick={handleSave}
                          style={{
                            backgroundColor: "var(--color-green)",
                            border: "none",
                            borderRadius: "25px",
                            color: "white",
                            minWidth: "120px",
                            transition: "all 0.3s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = "#3a9651";
                            e.target.style.transform = "translateY(-2px)";
                            e.target.style.boxShadow =
                              "0 6px 12px rgba(72, 176, 96, 0.3)";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor =
                              "var(--color-green)";
                            e.target.style.transform = "translateY(0)";
                            e.target.style.boxShadow = "none";
                          }}
                        >
                          ✅ Guardar
                        </button>
                        <button
                          className="btn btn-lg px-4 py-2 fw-bold"
                          onClick={handleCancel}
                          style={{
                            backgroundColor: "var(--color-beige)",
                            border: "none",
                            borderRadius: "25px",
                            color: "var(--color-dark)",
                            minWidth: "120px",
                            transition: "all 0.3s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = "#a69080";
                            e.target.style.transform = "translateY(-2px)";
                            e.target.style.boxShadow =
                              "0 6px 12px rgba(193, 168, 149, 0.3)";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor =
                              "var(--color-beige)";
                            e.target.style.transform = "translateY(0)";
                            e.target.style.boxShadow = "none";
                          }}
                        >
                          ❌ Cancelar
                        </button>
                      </>
                    )
                  ) : (
                    <div className="text-center">
                      <p className="text-muted small mb-2">
                        👤 Usuario autenticado con Google
                      </p>
                      <p className="text-info small">
                        Los datos se sincronizan automáticamente desde tu cuenta
                        de Google.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
